<template>
  <v-dialog v-if="canUserUseHaystack"
            v-model="haystackDialog"
            width="600px"
            persistent>
    <template v-slot:activator="{ on, attrs }">
      <v-btn class="mr-2"
             v-bind="attrs"
             v-on="on"
             text>
        <v-icon left>
          fas fa-fw fa-sparkles
        </v-icon>
        Ask AI
      </v-btn>
    </template>
    <template v-slot:default>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center primary white--text">
          <div>
            <v-icon left
                    dark>
              fas fa-fw fa-sparkles
            </v-icon>
            Mobile Locker AI Assistant (BETA)
          </div>
          <div>
            <v-btn href="https://help.mobilelocker.com/article/329-what-is-mobile-locker-ai-assistant"
                   target="_blank"
                   text
                   dark
                   class="mr-2">
              Help
            </v-btn>
            <v-btn @click.prevent="closeDialog"
                   text
                   dark>
              Close
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text class="py-4">
          <v-form @submit.prevent="submit"
                  :disabled="form.busy">
            <div class="d-flex justify-space-between align-center">
              <v-text-field v-model="form.question"
                            label="What is your question?"
                            placeholder="Ask a question about your content in Mobile Locker."
                            outlined
                            dense
                            hide-details
                            clearable
                            @click:clear="resetForm"
                            class="mr-2"
                            :disabled="form.busy"
                            :error-messages="form.errors.get('question')">
              </v-text-field>
              <v-btn @click.prevent="submit"
                     type="submit"
                     icon
                     color="primary"
                     class="primary"
                     dark>
                <v-progress-circular v-if="form.busy"
                                     indeterminate
                                     color="white">
                </v-progress-circular>
                <v-icon v-else
                        class="white--text">
                  fas fa-fw fa-arrow-up
                </v-icon>
              </v-btn>
            </div>
          </v-form>

          <!-- Enhanced Answer Section -->
          <div v-if="hasAnswer"
               class="mt-4">
            <v-divider class="mb-4"></v-divider>

            <!-- Answer Header with Confidence -->
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-icon left :color="answerFoundColor" class="mr-2">
                  {{ answerFoundIcon }}
                </v-icon>
                <span class="subtitle-1 font-weight-semibold" :class="answerFoundColor + '--text'">
                  {{ answerFoundText }}
                </span>
              </div>

              <!-- Confidence Badge -->
              <v-chip v-if="confidenceLevel"
                      small
                      :color="confidenceColor"
                      :text-color="confidenceTextColor"
                      class="confidence-chip">
                <v-icon small left>
                  {{ confidenceIcon }}
                </v-icon>
                {{ confidenceText }}
              </v-chip>
            </div>

            <!-- Answer Content Card -->
            <v-card flat
                    class="answer-content-card elevation-2 mb-4">
              <v-card-text class="py-4 px-4">
                <div class="answer-text body-1 text--primary">
                  {{ answerText }}
                </div>
              </v-card-text>
            </v-card>

            <!-- Sources Section -->
            <div v-if="hasValidSources" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon left color="primary" small class="mr-2">
                  fas fa-fw fa-file-alt
                </v-icon>
                <span class="caption font-weight-semibold text-uppercase primary--text">
                  Source{{ validSources.length > 1 ? 's' : '' }} ({{ validSources.length }})
                </span>
              </div>

              <v-row dense>
                <v-col v-for="(source, index) in validSources"
                       :key="index"
                       cols="12">
                  <v-card flat
                          outlined
                          class="source-card pa-2">
                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center">
                        <v-icon small :color="getRelevanceColor(source.relevance)" class="mr-2">
                          {{ getRelevanceIcon(source.relevance) }}
                        </v-icon>
                        <span class="body-2 font-weight-medium">
                          {{ source.presentation_name }} ({{ source.presentation_id }})
                        </span>
                      </div>
                      <v-chip x-small
                              :color="getRelevanceColor(source.relevance)"
                              :text-color="getRelevanceTextColor(source.relevance)"
                              class="relevance-chip">
                        {{ getRelevanceText(source.relevance) }}
                      </v-chip>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-end align-center mb-3">
              <v-btn @click.prevent="copyAnswer"
                     icon
                     small
                     color="grey darken-1"
                     title="Copy answer to clipboard"
                     class="mr-2 action-btn">
                <v-icon small>
                  fal fa-fw fa-copy
                </v-icon>
              </v-btn>
              <v-btn @click.prevent="thumbsUp"
                     icon
                     small
                     :color="response.rating === 5 ? 'success' : 'grey darken-1'"
                     title="Give positive feedback"
                     class="mr-2 action-btn">
                <v-icon small>
                  {{ response.rating === 5 ? 'fas fa-fw fa-thumbs-up' : 'fal fa-fw fa-thumbs-up' }}
                </v-icon>
              </v-btn>
              <v-btn @click.prevent="thumbsDown"
                     icon
                     small
                     :color="response.rating === 1 ? 'error' : 'grey darken-1'"
                     title="Give negative feedback"
                     class="action-btn">
                <v-icon small>
                  {{ response.rating === 1 ? 'fas fa-fw fa-thumbs-down' : 'fal fa-fw fa-thumbs-down' }}
                </v-icon>
              </v-btn>
            </div>

            <!-- Enhanced Disclaimer -->
            <v-alert type="warning"
                     outlined
                     dense
                     class="mb-0 disclaimer-alert"
                     icon="fas fa-fw fa-exclamation-triangle">
              <span class="caption">
                AI can make mistakes. Please double-check responses.
              </span>
            </v-alert>
          </div>
        </v-card-text>
        <ErrorAlert :error="error">
        </ErrorAlert>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import haystackService from '../../../services/haystackService';
import clipboardService from '../../../../../ml/services/clipboardService';
import {find} from 'lodash';
import ErrorAlert from '../../../components/ErrorAlert.vue';

export default {
  name: 'haystack-dialog',
  components: {ErrorAlert},
  data() {
    return {
      form: new Form({
        question: null,
      }),
      error: null,
      haystackDialog: false,
      response: null,
    }
  },
  computed: {
    ...mapGetters([
      'canUserUseHaystack',
    ]),
    ...mapGetters('content', [
      'presentations',
    ]),
    hasAnswer() {
      return this.response?.response_data?.answer;
    },
    answerData() {
      return this.response?.response_data?.answer;
    },
    answerText() {
      return this.answerData?.answer || null;
    },
    answerFound() {
      return this.answerData?.answer_found ?? false;
    },
    confidenceLevel() {
      return this.answerData?.confidence || null;
    },
    presentationIDs() {
      return this.presentations.map(p => p.id);
    },
    sources() {
      return this.answerData?.sources || [];
    },
    validSources() {
      return this.sources.filter(source => {
        return this.presentationIDs.includes(source.presentation_id);
      }).map(source => {
        return {
          presentation_id: source.presentation_id,
          relevance: source.relevance,
          presentation_name: this.getPresentationName(source.presentation_id),
        }
      })
    },
    hasValidSources() {
      return this.validSources.length > 0;
    },
    // Answer found status
    answerFoundColor() {
      return this.answerFound ? 'success' : 'warning';
    },
    answerFoundIcon() {
      return this.answerFound ? 'fas fa-fw fa-check-circle' : 'fas fa-fw fa-question-circle';
    },
    answerFoundText() {
      return this.answerFound ? 'Answer Found' : 'Limited Information';
    },
    // Confidence styling
    confidenceColor() {
      const colors = {
        'high': 'success',
        'medium': 'warning',
        'low': 'error'
      };
      return colors[this.confidenceLevel] || 'grey';
    },
    confidenceTextColor() {
      return this.confidenceLevel === 'medium' ? 'black' : 'white';
    },
    confidenceIcon() {
      const icons = {
        'high': 'fas fa-fw fa-check-double',
        'medium': 'fas fa-fw fa-exclamation',
        'low': 'fas fa-fw fa-question'
      };
      return icons[this.confidenceLevel] || 'fas fa-fw fa-question';
    },
    confidenceText() {
      const texts = {
        'high': 'High Confidence',
        'medium': 'Medium Confidence',
        'low': 'Low Confidence'
      };
      return texts[this.confidenceLevel] || 'Unknown';
    }
  },
  methods: {
    getPresentationName(presentationID) {
      const presentation = find(this.presentations, p => p.id === parseInt(presentationID));
      return presentation?.name || null;
    },
    closeDialog() {
      this.resetForm();
      this.haystackDialog = false;
    },
    resetForm() {
      this.form.reset();
      this.response = null;
      this.error = null;
    },
    async submit() {
      if (this.form.busy) {
        return;
      }
      try {
        this.error = null;
        this.response = null;
        this.response = await haystackService.askQuestion(this.form)
      } catch (e) {
        this.response = null;
        this.error = e;
      }
    },
    async copyAnswer() {
      if (this.answerText) {
        await clipboardService.copy(this.answerText)
        this.$toast.success('Answer copied to clipboard.');
      }
    },
    async thumbsUp() {
      if (!this.response?.id) {
        return;
      }
      try {
        this.response = await haystackService.rate(this.response.id, 5);
      } catch (e) {
      }
    },
    async thumbsDown() {
      if (!this.response?.id) {
        return;
      }
      try {
        this.response = await haystackService.rate(this.response.id, 1);
      } catch (e) {
      }
    },
    // Source relevance helpers
    getRelevanceColor(relevance) {
      const colors = {
        'direct': 'success',
        'supporting': 'info',
        'tangential': 'warning'
      };
      return colors[relevance] || 'grey';
    },
    getRelevanceTextColor(relevance) {
      return relevance === 'tangential' ? 'black' : 'white';
    },
    getRelevanceIcon(relevance) {
      const icons = {
        'direct': 'fas fa-fw fa-bullseye',
        'supporting': 'fas fa-fw fa-hands-helping',
        'tangential': 'fas fa-fw fa-link'
      };
      return icons[relevance] || 'fas fa-fw fa-question';
    },
    getRelevanceText(relevance) {
      const texts = {
        'direct': 'Direct',
        'supporting': 'Supporting',
        'tangential': 'Related'
      };
      return texts[relevance] || 'Unknown';
    }
  }
}
</script>

<style lang="scss" scoped>
.answer-content-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-left: 4px solid #4caf50;
  border-radius: 8px !important;
}

.answer-text {
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.95rem;
  max-height: 300px;
  overflow-y: auto;
}

/* Source cards styling */
.source-card {
  background-color: rgba(33, 150, 243, 0.04);
  border-color: rgba(33, 150, 243, 0.2) !important;
  border-radius: 6px !important;
  transition: all 0.2s ease;
}

.source-card:hover {
  background-color: rgba(33, 150, 243, 0.08);
  transform: translateY(-1px);
}

/* Chip styling */
.confidence-chip {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.relevance-chip {
  font-size: 10px !important;
  height: 20px !important;
  font-weight: 600;
}

/* Action buttons styling */
.action-btn {
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

/* Disclaimer alert styling */
.disclaimer-alert {
  background-color: rgba(255, 193, 7, 0.05) !important;
  border-color: rgba(255, 193, 7, 0.3) !important;
}

.disclaimer-alert .v-alert__icon {
  color: #ff9800 !important;
}

/* Custom scrollbar for long answers */
.answer-text::-webkit-scrollbar {
  width: 4px;
}

.answer-text::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.answer-text::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.answer-text::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Improve visual hierarchy */
.v-divider {
  margin: 0 -24px;
}

/* Animation for sources */
.source-card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
